/**
 * @fileoverview Estilos dos modais da cobrança (redução, escolha do que fica,
 * estorno) e dos estados depois de pagar: a moldura das telas novas (dialogShell),
 * as escolhas à esquerda e o resumo à direita, fixo enquanto a coluna da esquerda
 * rola. O layout da contratação em duas etapas está em checkoutStyles.js.
 * Monocromático; o azul só no botão principal, e cor só nos estados (pago, recusado).
 */

import { makeStyles } from "@material-ui/core/styles";

import { dialogShell } from "../../../styles/dialogShell";
import { tokens } from "../../Triage/styles";

const usePlansDialogStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  const c = tokens(dark);
  const text = dark ? "#FAFAFA" : "#09090B";
  const muted = dark ? "#A1A1AA" : "#52525B";
  const line = dark ? "rgba(255,255,255,0.08)" : "#E4E4E7";
  const soft = dark ? "rgba(255,255,255,0.03)" : "#FAFAFA";
  const focus = `0 0 0 1px ${dark ? "rgba(255,255,255,0.7)" : "rgba(9,9,11,0.7)"}`;
  const reduce = "@media (prefers-reduced-motion: reduce)";

  return {
    ...dialogShell(theme, { width: 940 }),
    body: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr) 320px",
      minHeight: 420,
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
    },
    choices: {
      padding: "20px 22px 24px",
      display: "flex",
      flexDirection: "column",
      gap: 22,
      maxHeight: "min(68vh, 640px)",
      overflowY: "auto",
      [theme.breakpoints.down("sm")]: { maxHeight: "none" },
    },
    group: { display: "flex", flexDirection: "column", gap: 10 },
    groupHead: { display: "flex", alignItems: "center", gap: 4 },
    groupTitle: { fontSize: 13.5, fontWeight: 650, color: text },

    // Basic ou Pro, na redução: duas linhas escolhíveis, a marcada com o fundo um tom acima.
    planOptions: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" } },
    planOption: {
      textAlign: "left",
      fontFamily: "inherit",
      cursor: "pointer",
      border: `1px solid ${line}`,
      borderRadius: 12,
      background: "transparent",
      color: text,
      padding: "14px 16px",
      display: "flex",
      flexDirection: "column",
      gap: 6,
      transition: "background 140ms ease, border-color 140ms ease",
      "&:hover": { background: soft },
      "&:focus-visible": { outline: "none", boxShadow: focus },
      [reduce]: { transition: "none" },
    },
    planOptionOn: {
      background: dark ? "rgba(255,255,255,0.06)" : "#F4F4F5",
      borderColor: dark ? "rgba(255,255,255,0.24)" : "#A1A1AA",
    },
    planName: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, fontSize: 15, fontWeight: 700 },
    radioDot: {
      width: 16,
      height: 16,
      borderRadius: "50%",
      border: `1.5px solid ${dark ? "rgba(255,255,255,0.4)" : "#71717A"}`,
      flexShrink: 0,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
    },
    radioDotOn: {
      borderColor: text,
      "&::after": { content: '""', width: 8, height: 8, borderRadius: "50%", background: text },
    },
    planPrice: { fontSize: 13, color: text, fontVariantNumeric: "tabular-nums" },
    // "Atual" ao lado do nome, em cinza (sem selo colorido).
    planCurrent: { marginLeft: 8, fontSize: 12, fontWeight: 500, color: muted },

    // Licenças e Chat Interno: o número entre o menos e o mais.
    // O cancelamento (etapa 9): ficar com o Chat Interno no Starter.
    keepRow: { display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer", "& > span": { display: "flex", flexDirection: "column", gap: 2 } },
    keepBox: { margin: "2px 0 0", width: 16, height: 16, flexShrink: 0, accentColor: text, cursor: "pointer", "&:focus-visible": { outline: "none", boxShadow: focus } },
    keepHint: { fontSize: 12.5, lineHeight: 1.5, color: muted },
    stepperRow: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" },
    stepperLabel: { display: "flex", flexDirection: "column", gap: 2 },
    stepperName: { display: "flex", alignItems: "center", gap: 4, fontSize: 13.5, fontWeight: 650, color: text },
    stepperHint: { fontSize: 12.5, color: muted, fontVariantNumeric: "tabular-nums" },
    stepper: { display: "inline-flex", alignItems: "center", border: `1px solid ${c.inputBorder}`, borderRadius: 10, overflow: "hidden", flexShrink: 0 },
    stepButton: {
      width: 40,
      height: 40,
      border: "none",
      background: "transparent",
      color: text,
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      "& svg": { width: 16, height: 16 },
      transition: "background 120ms ease",
      "&:hover:not([aria-disabled='true'])": { background: dark ? "rgba(255,255,255,0.08)" : "#F4F4F5" },
      "&:active:not([aria-disabled='true'])": { background: dark ? "rgba(255,255,255,0.12)" : "#E4E4E7" },
      [reduce]: { transition: "none" },
      "&[aria-disabled='true']": { color: dark ? "rgba(255,255,255,0.25)" : "#A1A1AA", cursor: "default" },
      "&:focus-visible": { outline: "none", boxShadow: `inset ${focus}` },
    },
    // Dá para digitar o número: o campo não parece um campo até receber o foco.
    stepValue: {
      width: 48,
      height: 40,
      padding: 0,
      border: "none",
      outline: "none",
      background: "transparent",
      textAlign: "center",
      fontFamily: "inherit",
      fontSize: 15,
      fontWeight: 650,
      color: text,
      fontVariantNumeric: "tabular-nums",
      "&:focus": { background: dark ? "rgba(255,255,255,0.08)" : "#F4F4F5" },
    },

    methodNote: { fontSize: 12.5, lineHeight: 1.5, color: muted, margin: 0 },
    label: { fontSize: 12.5, fontWeight: 600, color: text },
    input: {
      height: 40,
      borderRadius: 8,
      border: `1px solid ${c.inputBorder}`,
      background: dark ? "rgba(255,255,255,0.04)" : "#fff",
      color: text,
      padding: "0 12px",
      fontSize: 14,
      fontFamily: "inherit",
      fontVariantNumeric: "tabular-nums",
      width: "100%",
      boxSizing: "border-box",
      outline: "none",
      "&:focus": { borderColor: dark ? "rgba(255,255,255,0.7)" : "#09090B" },
      "&::placeholder": { color: dark ? "rgba(250,250,250,0.32)" : "#A1A1AA" },
    },

    // O resumo do mês, fixo à direita.
    summary: {
      borderLeft: `1px solid ${line}`,
      background: soft,
      padding: "20px 22px 22px",
      display: "flex",
      flexDirection: "column",
      gap: 14,
      [theme.breakpoints.down("sm")]: { borderLeft: "none", borderTop: `1px solid ${line}` },
    },
    // No celular, o resumo fica só com o total e o botão (os itens já estão nas escolhas, acima).
    summaryTitle: { margin: 0, fontSize: 13.5, fontWeight: 650, color: text, [theme.breakpoints.down("xs")]: { display: "none" } },
    summaryLines: { listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 10, [theme.breakpoints.down("xs")]: { display: "none" } },
    summaryLine: { display: "flex", justifyContent: "space-between", gap: 12, fontSize: 13, color: muted, fontVariantNumeric: "tabular-nums" },
    summaryLineValue: { color: text, whiteSpace: "nowrap" },
    totalRow: { display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, paddingTop: 12, borderTop: `1px solid ${line}` },
    totalLabel: { fontSize: 13, color: muted, minWidth: 0 },
    // O valor nunca encolhe nem passa da borda: quem quebra a linha é o rótulo ao lado.
    totalValue: { fontSize: 24, fontWeight: 700, letterSpacing: "-0.01em", color: text, fontVariantNumeric: "tabular-nums", flexShrink: 0, whiteSpace: "nowrap" },
    spacer: { flex: 1 },
    notice: { fontSize: 12.5, lineHeight: 1.5, color: c.warn, margin: 0 },
    noticeAction: {
      alignSelf: "flex-start",
      border: "none",
      background: "transparent",
      padding: 0,
      fontFamily: "inherit",
      fontSize: 12.5,
      fontWeight: 600,
      color: text,
      textDecoration: "underline",
      textUnderlineOffset: 3,
      cursor: "pointer",
      "&:focus-visible": { outline: "none", boxShadow: focus },
    },
    formError: { fontSize: 12.5, color: c.bad, margin: 0 },
    // A cotação: enquanto recalcula, o valor anterior fica esmaecido (sem pular de altura a cada clique).
    quoteBlock: { display: "flex", flexDirection: "column", gap: 14, transition: "opacity 140ms ease", [reduce]: { transition: "none" } },
    quoteBusy: { opacity: 0.45 },
    quoteToday: { margin: 0, fontSize: 13, color: muted, fontVariantNumeric: "tabular-nums" },
    quoteWaiting: { alignItems: "center", justifyContent: "flex-start", color: muted },
    quoteProblem: { display: "flex", flexDirection: "column", gap: 6, paddingTop: 12, borderTop: `1px solid ${line}` },
    stateHint: { margin: 0, fontSize: 12.5, lineHeight: 1.5, color: muted, maxWidth: 440 },
    primaryInline: { padding: "0 22px" },
    primary: {
      height: 44,
      borderRadius: 10,
      border: "none",
      background: "#3D5CFD",
      color: "#fff",
      fontFamily: "inherit",
      fontSize: 14,
      fontWeight: 700,
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      fontVariantNumeric: "tabular-nums",
      "&:disabled": { opacity: 0.55, cursor: "default" },
      "&:focus-visible": { outline: "none", boxShadow: `0 0 0 1px ${dark ? "#fff" : "#09090B"}` },
    },
    secondary: {
      height: 38,
      borderRadius: 9,
      border: `1px solid ${line}`,
      background: "transparent",
      color: text,
      fontFamily: "inherit",
      fontSize: 13.5,
      fontWeight: 600,
      padding: "0 16px",
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      textDecoration: "none",
      // O link com cara de botão (abrir o boleto): o public/index.html pinta todo <a> de verde com
      // !important (os links das mensagens); aqui vale a cor do texto, também visitado e no hover.
      "&:link, &:visited, &[href]:hover": { color: `${text} !important` },
      "&:hover": { background: soft },
      "&:focus-visible": { outline: "none", boxShadow: focus },
      "& svg": { width: 15, height: 15 },
    },

    // Depois de pagar: acompanhando, pago, recusado ou o boleto.
    state: { padding: "44px 28px 36px", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 12, minHeight: 300, justifyContent: "center" },
    stateIcon: {
      width: 48,
      height: 48,
      borderRadius: "50%",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      animation: "$pop 220ms cubic-bezier(0.2, 0.9, 0.3, 1.2)",
      "& svg": { width: 24, height: 24 },
      [reduce]: { animation: "none" },
    },
    stateOk: { color: c.ok, background: dark ? "rgba(0,168,132,0.12)" : "rgba(0,113,92,0.08)" },
    stateBad: { color: c.bad, background: dark ? "rgba(241,92,109,0.12)" : "rgba(198,40,40,0.07)" },
    stateTitle: { margin: 0, fontSize: 18, fontWeight: 700, color: text },
    stateText: { margin: 0, fontSize: 14, lineHeight: 1.55, color: muted, maxWidth: 440 },
    // Os botões dos estados com a mesma altura do principal.
    stateActions: { display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center", marginTop: 8, "& > button, & > a": { height: 44 } },
    boletoLine: { marginTop: 4, width: "100%", maxWidth: 560, display: "flex", alignItems: "center", justifyContent: "center", gap: 12, flexWrap: "wrap" },
    boletoDigits: { fontSize: 14, color: text, fontVariantNumeric: "tabular-nums", letterSpacing: "0.01em", wordBreak: "break-word" },
    "@keyframes pop": { from: { transform: "scale(0.85)", opacity: 0 }, to: { transform: "scale(1)", opacity: 1 } },
  };
});

export default usePlansDialogStyles;
